import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { listEntries } from "../catalogs/api";
import * as api from "./api";

// SPEC-213: buscar miembro. Sin texto, no se dispara ninguna consulta —
// mismo criterio que RF-GCM-01 (sin texto ingresado no se muestra ningún
// resultado): no tiene sentido cachear el universo completo de miembros acá.
export const useMemberSearch = (query: string) =>
  useQuery({
    queryKey: ["portal", "circle-member-search", query.trim()],
    queryFn: () => api.searchMembers(query),
    enabled: query.trim().length > 0,
  });

export const useReferralCircle = (memberId: string | null) =>
  useQuery({
    queryKey: ["portal", "referral-circle", memberId],
    queryFn: () => api.getReferralCircle(memberId!),
    enabled: Boolean(memberId),
  });

export function useCircleMutations(memberId: string | null) {
  const client = useQueryClient();
  const invalidate = () => {
    void client.invalidateQueries({
      queryKey: ["portal", "referral-circle", memberId],
    });
  };
  return {
    create: useMutation({
      mutationFn: (input: api.ReferralInput) =>
        api.createReferral(memberId!, input),
      onSuccess: invalidate,
    }),
    edit: useMutation({
      mutationFn: ({
        referralId,
        input,
      }: {
        referralId: string;
        input: api.ReferralInput;
      }) => api.editReferral(referralId, input),
      onSuccess: invalidate,
    }),
    inactivate: useMutation({
      mutationFn: (referralId: string) => api.inactivateReferral(referralId),
      onSuccess: invalidate,
    }),
    activate: useMutation({
      mutationFn: (referralId: string) => api.activateReferral(referralId),
      onSuccess: invalidate,
    }),
  };
}

export const useReferralDetail = (referralId: string | null) =>
  useQuery({
    queryKey: ["portal", "referral", referralId],
    queryFn: () => api.getReferralDetail(referralId!),
    enabled: Boolean(referralId),
  });

// Catálogo "acciones del referido" (SPEC-011). Se resuelve acá, no con un
// `useQuery` suelto dentro del panel, mismo criterio que el resto de las
// features del portal (las llamadas a TanStack Query viven en `*Queries.ts`).
export const useReferralActionCatalog = () =>
  useQuery({
    queryKey: ["portal", "catalog-entries", "referral-actions"],
    queryFn: () =>
      listEntries("referral-actions", { includeInactive: false, search: "" }),
  });

// `progressId` es opcional: cuando el panel se abre desde el avance del
// Gran Premio (SPEC-209 §4/§5), registrar o anular una acción cambia el
// avance del miembro y hay que invalidar su detalle además del propio
// referido. Cuando se abre desde SPEC-213 (círculo del miembro) no hay
// avance que invalidar.
//
// `memberId` (bugs #28/#29 de la matriz QA, 2026-09): la etapa del referido
// no es una columna persistida — se deriva en cada lectura de
// `["portal","referral-circle", memberId]` (`useReferralCircle`), la query
// que alimenta la tabla del círculo del miembro. Sin invalidarla acá, la
// tabla queda con la etapa vieja hasta un refetch manual, aunque el detalle
// del referido y el avance del Gran Premio ya se hayan actualizado. Es
// opcional porque no todo llamador conoce el `memberId` del referido (p.ej.
// si en el futuro se abre este panel sin haber resuelto el miembro).
export function useReferralMutations(
  referralId: string,
  progressId?: string,
  memberId?: string,
) {
  const client = useQueryClient();
  const invalidate = () => {
    void client.invalidateQueries({
      queryKey: ["portal", "referral", referralId],
    });
    if (progressId) {
      void client.invalidateQueries({
        queryKey: ["portal", "grand-prize-progress"],
      });
      void client.invalidateQueries({
        queryKey: ["portal", "grand-prize-progress-detail", progressId],
      });
    }
    if (memberId) {
      void client.invalidateQueries({
        queryKey: ["portal", "referral-circle", memberId],
      });
    }
  };
  return {
    register: useMutation({
      mutationFn: ({
        input,
        idempotencyKey,
      }: {
        input: api.RegisterReferralActionInput;
        idempotencyKey: string;
      }) => api.registerReferralAction(referralId, input, idempotencyKey),
      onSuccess: invalidate,
    }),
    void: useMutation({
      mutationFn: ({
        actionId,
        input,
      }: {
        actionId: string;
        input: api.VoidReferralActionInput;
      }) => api.voidReferralAction(actionId, input),
      onSuccess: invalidate,
    }),
  };
}
